import { BrowserRouter, Routes, Route, Navigate, useLocation, useParams } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import Layout from "@/components/Layout";
import Landing from "@/pages/Landing";
import Demo from "@/pages/Demo";
import Login from "@/pages/Login";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import RequestAccess from "@/pages/RequestAccess";
import Activate from "@/pages/Activate";
import PlatformAdmin from "@/pages/PlatformAdmin";
import LegalNotice from "@/pages/legal/LegalNotice";
import Privacy from "@/pages/legal/Privacy";
import Terms from "@/pages/legal/Terms";
import Cookies from "@/pages/legal/Cookies";
import Dpa from "@/pages/legal/Dpa";
import Dashboard from "@/pages/Dashboard";
import MachineForm from "@/pages/MachineForm";
import MachinePassport from "@/pages/MachinePassport";
import Locations from "@/pages/Locations";
import Users from "@/pages/Users";
import Settings from "@/pages/Settings";
import SecurityPage from "@/pages/Security";
import "@/App.css";

function ProtectedRoute({ children }) {
  const { user } = useAuth();
  const location = useLocation();

  if (user === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-zinc-50">
        <div className="h-8 w-8 rounded-full border-2 border-zinc-300 border-t-zinc-900 animate-spin" data-testid="loading-spinner" />
      </div>
    );
  }
  if (user === false) {
    const redirect = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/login?redirect=${redirect}`} replace />;
  }
  return children;
}

function QrRedirect() {
  const { id } = useParams();
  return <Navigate to={`/app/machines/${id}`} replace />;
}

function AdminOnly({ children }) {
  const { user } = useAuth();
  if (user && user.role !== "admin") {
    return <Navigate to="/app" replace />;
  }
  return children;
}

function PlatformOnly({ children }) {
  const { user } = useAuth();
  if (user && !user.platform_admin) {
    return <Navigate to="/app" replace />;
  }
  return children;
}

import { IS_PREVIEW_HOST, PREVIEW_BANNER_TEXT } from "@/lib/env";
import VersionGuard from "@/components/VersionGuard";

// Indicador de entorno: evita confusiones preview vs producción (los datos del
// preview viven en otra base de datos que la de assetpassport.io).
function PreviewBanner() {
  if (!IS_PREVIEW_HOST) return null;
  return (
    <div
      data-testid="preview-env-banner"
      className="fixed bottom-0 inset-x-0 z-[200] bg-amber-100/95 border-t border-amber-300 text-amber-900 text-xs font-medium text-center py-1.5 px-4 backdrop-blur-sm"
    >
      {PREVIEW_BANNER_TEXT}
    </div>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <Toaster position="top-center" richColors />
          <PreviewBanner />
          <VersionGuard />
          <Routes>
            <Route path="/" element={<Landing />} />
            <Route path="/demo" element={<Demo />} />
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Navigate to="/solicitar-acceso" replace />} />
            <Route path="/solicitar-acceso" element={<RequestAccess />} />
            <Route path="/activate" element={<Activate />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/security" element={<SecurityPage />} />
            <Route path="/legal" element={<LegalNotice />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/cookies" element={<Cookies />} />
            <Route path="/dpa" element={<Dpa />} />
            <Route path="/m/:id" element={<ProtectedRoute><QrRedirect /></ProtectedRoute>} />
            <Route
              path="/app"
              element={
                <ProtectedRoute>
                  <Layout />
                </ProtectedRoute>
              }
            >
              <Route index element={<Dashboard />} />
              <Route path="machines/new" element={<MachineForm />} />
              <Route path="machines/:id" element={<MachinePassport />} />
              <Route path="machines/:id/edit" element={<MachineForm />} />
              <Route path="locations" element={<Locations />} />
              <Route path="users" element={<AdminOnly><Users /></AdminOnly>} />
              <Route path="settings" element={<AdminOnly><Settings /></AdminOnly>} />
            </Route>
            <Route path="/platform" element={<ProtectedRoute><PlatformOnly><PlatformAdmin /></PlatformOnly></ProtectedRoute>} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
